import { Button, Input, Text } from '@components/ui';
import { useAddInvitationMutation } from '@lib/api/auth';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';

import type { InvitationLinkState, StoredUser } from './sendEmailTypes';
interface SendEmailProps {
  open: boolean;
  handleCloseSendEmailModal: () => void;
}

const AddCoupon = ({
  open,
  handleCloseSendEmailModal,
}: SendEmailProps): JSX.Element => {
  const { t } = useTranslation();
  const storedUser = localStorage.getItem('user');
  const user = (
    storedUser ? JSON.parse(storedUser) : null
  ) as StoredUser | null;
  const [state, setState] = useState<InvitationLinkState>({
    value: window.location.href + user?.user?.id,
    copied: false,
  });

  const handleClose = (): void => {
    handleCloseSendEmailModal();
  };

  const addInvitation = useAddInvitationMutation();

  const handleInvitationLink = (): void => {
    addInvitation.mutate(state.value);
  };

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="invitation-dialog-title"
      open={open}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '14px',
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          color: 'text.primary',
        },
      }}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'center',
          justifyContent: 'center',
        },
      }}
    >
      <DialogTitle
        id="invitation-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          px: 3,
          pt: 2.5,
          pb: 2,
          fontSize: '1rem',
          fontWeight: 700,
          letterSpacing: '0.4px',
          textTransform: 'uppercase',
        }}
      >
        {t('auth.invitationLink')}
        <IconButton
          type="button"
          aria-label={t('common.close')}
          onClick={handleClose}
          sx={{
            p: 0.5,
            color: 'text.secondary',
            '&:hover': { color: 'text.primary' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 3 }}>
        <Text
          variant="label"
          tone="muted"
          sx={{
            textTransform: 'uppercase',
            letterSpacing: '0.3px',
            fontSize: 13,
          }}
        >
          {t('auth.copyAndShare')}
        </Text>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            gap: 1.25,
            borderRadius: '10px',
            border: 1,
            borderColor: 'divider',
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? '#262a2e' : '#f7f8fa',
            px: 1.25,
            py: 1,
            flexDirection: 'column',
            '@media (min-width: 560px)': {
              flexDirection: 'row',
              alignItems: 'center',
            },
          }}
        >
          <Input
            disabled
            value={state.value}
            onChange={(event) =>
              setState({ value: event.target.value, copied: false })
            }
            sx={{
              flex: 1,
              minWidth: 0,
              '& .MuiInputBase-root': { bgcolor: 'transparent' },
              '& .MuiInputBase-root.Mui-disabled': { opacity: 1 },
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '& .MuiInputBase-input.Mui-disabled': {
                color: 'text.secondary',
                WebkitTextFillColor: 'unset',
              },
            }}
          />
          <CopyToClipboard
            text={state.value}
            onCopy={() => setState((current) => ({ ...current, copied: true }))}
          >
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleInvitationLink}
              sx={{
                flexShrink: 0,
                height: 36,
                textTransform: 'uppercase',
                fontSize: 13,
                width: '100%',
                '@media (min-width: 560px)': { width: 'auto' },
                ...(state.copied
                  ? {
                      bgcolor: 'success.main',
                      '&:hover': { bgcolor: 'success.main' },
                    }
                  : {}),
              }}
            >
              {state.copied ? 'Copied!' : 'Copy'}
            </Button>
          </CopyToClipboard>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddCoupon;
